import { useCallback } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { useNoticeBannersStackContext } from '@sg/garnish';

import { useTelemetry } from '@order/Telemetry';

import { useRequestAccountDeletionMutation } from '../../graphql/RequestAccountDeletion.generated';

export const useAccountDeletion = (props: UseAccountDeletionProps) => {
  const { onClose, onSuccess } = props;

  const { track } = useTelemetry();
  const { formatMessage } = useIntl();
  const { push: addNoticeBanner } = useNoticeBannersStackContext();

  const [deleteAccountResponse, deleteAccount] =
    useRequestAccountDeletionMutation();
  const isDeletingAccount = deleteAccountResponse.fetching;

  const requestAccountDeletion = useCallback(async () => {
    track('account.profile_request_deletion_confirmed');
    // @ts-expect-error TS(2554): Expected 1-2 arguments, but got 0.
    const { data } = await deleteAccount();
    const result = data?.requestAccountDeletion?.__typename;

    if (result !== 'RequestAccountDeletionSuccess') {
      addNoticeBanner({
        text: formatMessage(messages.accountDeleteError),
        palette: 'caution',
      });

      return;
    }

    addNoticeBanner({
      text: formatMessage(messages.accountDeleteSuccess),
      palette: 'success',
    });
    onClose();
    onSuccess();
  }, [
    track,
    formatMessage,
    deleteAccount,
    addNoticeBanner,
    onClose,
    onSuccess,
  ]);

  return { isDeletingAccount, requestAccountDeletion };
};

type UseAccountDeletionProps = Readonly<{
  onSuccess: () => void;
  onClose: () => void;
}>;

// ─── Messages ────────────────────────────────────────────────────────────────

const messages = defineMessages({
  accountDeleteError: {
    defaultMessage: 'Something went wrong with deleting your account.',
    description: 'Account | Delete | Error',
  },
  accountDeleteSuccess: {
    defaultMessage: 'You have successfully requested to delete your account.',
    description: 'Account | Delete | Success',
  },
});
